"use client";

// Paytm's CheckoutJS script is merchant (mid) and host (sandbox/production) specific, unlike
// Razorpay's fixed checkout.js URL -- so it's loaded fresh per checkout attempt rather than
// cached by a single module-level promise the way lib/razorpay.ts's script loader is.
function loadCheckoutScript(host: string, mid: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.src = `${host}/merchantpgpui/checkoutjs/merchants/${encodeURIComponent(mid)}.js`;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("Could not load Paytm Checkout."));
    document.body.appendChild(script);
  });
}

/**
 * Opens Paytm's CheckoutJS overlay and resolves once the overlay reports the transaction is
 * done. `merchant.redirect: false` + `handler.transactionStatus` (config nested under
 * data/merchant/handler, verified against
 * paytmpayments.com/docs/js-checkout/configuration/options/) keeps the flow in an overlay
 * rather than Paytm's own server-configured redirect, so this app controls navigation
 * afterward.
 *
 * Deliberately does NOT branch on the callback payload's success/failure field -- Paytm's
 * current docs don't pin down that field's exact name for the client-side callback (only the
 * server-side Transaction Status API's `resultStatus` is confirmed), and this codebase's own
 * design principle already covers exactly this situation: the webhook is authoritative, never
 * the client-side signal (see concurrency-and-edge-cases.md Part 4, rule 1). This resolves once
 * the overlay is done either way; the real outcome is decided by /book/confirmation polling
 * getBookingStatus, same as every other gateway here.
 */
export function openPaytmCheckout(checkout: {
  host: string;
  mid: string;
  orderId: string;
  txnToken: string;
  amountRupees: number;
}): Promise<void> {
  return loadCheckoutScript(checkout.host, checkout.mid).then(
    () =>
      new Promise<void>((resolve, reject) => {
        window.Paytm.CheckoutJS.init({
          flow: "DEFAULT",
          data: {
            orderId: checkout.orderId,
            token: checkout.txnToken,
            tokenType: "TXN_TOKEN",
            amount: checkout.amountRupees.toFixed(2),
          },
          merchant: {
            mid: checkout.mid,
            redirect: false,
          },
          handler: {
            transactionStatus: () => resolve(),
          },
        })
          .then(() => window.Paytm.CheckoutJS.invoke())
          .catch(reject);
      })
  );
}
